import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@weezevent/nacre';
import { isInvalidEmail } from '../../../utils';
import Row from './Row';
import QuotasExceeded from '../../QuotasExceeded';
import css from './table.module.css';

export default function AttendeesTable({ form, eventId, invitationsQuotas, attendees, setAttendees }) {
    const { t } = useTranslation();

    // Max possible orders, to prevent organizers to send 500/1000 orders to Pyvar, which can each include 50 attendees
    // To prevent timeouts
    const maxAttendees = 101;

    const onPaste = React.useCallback(
        event => {
            event.preventDefault();

            let clip = event.clipboardData.getData('Text');
            let row = clip.split('\n');
            let rows = [];
            let tab_name = ['lastname', 'firstname', 'quantity', 'email'];
            let paste_name = event.target.name;
            let paste_index = parseInt(event.target.dataset.id);

            row.forEach(r => {
                if (!r || !r.trim() || !r.replace(/\t/g, '').trim()) {
                    return;
                }
                let index = tab_name.findIndex(name => name === paste_name);
                let existingData = [];

                const pasted_data = r.split('\t');

                for (let i = 0; i < pasted_data.length; i++) {
                    existingData[index + i] = pasted_data[i];
                }
                rows.push(existingData);
            });

            setAttendees(prevAttendees => {
                let updatedAttendees = [...prevAttendees];
                const remainingSlots = maxAttendees - updatedAttendees.length;

                for (let i = 0; i < rows.length && i <= remainingSlots; i++) {
                    const [lastname, firstname, quantity, email] = rows[i];
                    const currentIndex = paste_index + i;

                    const email_value = email ? email.trim() : updatedAttendees[currentIndex]?.email;
                    updatedAttendees[currentIndex] = {
                        lastname: lastname ? lastname.trim() : updatedAttendees[currentIndex]?.lastname,
                        firstname: firstname ? firstname.trim() : updatedAttendees[currentIndex]?.firstname,
                        quantity: quantity ? quantity.trim() : updatedAttendees[currentIndex]?.quantity,
                        email: email_value,
                        event_id: eventId,
                        rate_id: form.selected_rates[0]?.rateId,
                        channel_id: 0,
                        payment_type: 9,
                        valid: isInvalidEmail(email_value),
                        paid_price: 0,
                        session_uuid: form.use_sessions ? form.sessions[0]?.value : null
                    };
                    updatedAttendees[currentIndex]['valid'] = email_value ? !isInvalidEmail(email_value) : true;
                    // Update dynamic form fields
                    ['form', 'buyer_form'].forEach(field => {
                        updatedAttendees[currentIndex][field] = {
                            ws_2: updatedAttendees[currentIndex].lastname,
                            ws_3: updatedAttendees[currentIndex].firstname,
                            ws_5: updatedAttendees[currentIndex].email
                        };
                    });
                }

                // Ensure there's always an empty row at the end, but never exceed maxAttendees
                if (
                    updatedAttendees.length < maxAttendees &&
                    (updatedAttendees.length === 0 || Object.values(updatedAttendees[updatedAttendees.length - 1]).some(value => value))
                ) {
                    updatedAttendees.push({});
                }

                return updatedAttendees;
            });
        },
        [isInvalidEmail, eventId, form]
    );

    const onChange = React.useCallback(
        (index, key, value) => {
            setAttendees(prevAttendees => {
                let updatedAttendees = [...prevAttendees];
                // Update the current attendee's data
                let attendeeToParse = {
                    lastname: updatedAttendees[index].lastname,
                    firstname: updatedAttendees[index].firstname,
                    quantity: updatedAttendees[index].quantity,
                    email: updatedAttendees[index].email,
                    event_id: eventId,
                    rate_id: form.selected_rates[0]?.rateId,
                    channel_id: 0,
                    payment_type: 9,
                    paid_price: 0,
                    session_uuid: form.use_sessions ? form.sessions[0].value : null
                };
                attendeeToParse[key] = value;
                attendeeToParse['valid'] = attendeeToParse.email ? !isInvalidEmail(attendeeToParse['email']) : true;

                ['form', 'buyer_form'].forEach(field => {
                    attendeeToParse[field] = {
                        ws_2: attendeeToParse.lastname,
                        ws_3: attendeeToParse.firstname,
                        ws_5: attendeeToParse.email
                    };
                });

                updatedAttendees[index] = attendeeToParse;

                // Check if the row is completely empty (check only the relevant fields)
                const isRowEmpty = !attendeeToParse.firstname && !attendeeToParse.lastname && !attendeeToParse.quantity && !attendeeToParse.email;

                updatedAttendees[index] = isRowEmpty ? {} : attendeeToParse;
                // Add a new empty row if it's the last row and it's not empty
                if (index === updatedAttendees.length - 1 && !isRowEmpty && updatedAttendees.length < maxAttendees) {
                    updatedAttendees.push({});
                }

                // Remove extra empty rows, keep only one at the end
                updatedAttendees = updatedAttendees.filter((a, i, arr) => a.firstname || a.lastname || a.email || a.quantity || i === arr.length - 1);

                return updatedAttendees;
            });
        },
        [setAttendees, eventId, isInvalidEmail, form, maxAttendees]
    );

    return (
        <>
            {!invitationsQuotas && <QuotasExceeded />}
            <Table className={css['manual-table']}>
                <Table.Header>
                    <Table.HeaderRow>
                        <Table.HeaderCell className={`${css['thead']} ${css['index']}`} />
                        <Table.HeaderCell className={`${css['thead']} ${css['infos']}`}>{t('attendees.invitations.table.lastName')}</Table.HeaderCell>
                        <Table.HeaderCell className={`${css['thead']} ${css['infos']}`}>{t('attendees.invitations.table.firstName')}</Table.HeaderCell>
                        <Table.HeaderCell className={`${css['thead']} ${css['qt']}`}>{t('attendees.invitations.table.quantity')}</Table.HeaderCell>
                        <Table.HeaderCell className={`${css['thead']}`}>{t('attendees.invitations.table.email')}</Table.HeaderCell>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {attendees.map((attendee, index) => (
                        <Row key={index} index={index} onPaste={onPaste} attendee={attendee} onChange={(key, value) => onChange(index, key, value)} sendMail={form.send_mail} />
                    ))}
                </Table.Body>
            </Table>
        </>
    );
}
